import { apiClient } from "@/lib/api-client";
import type { PagedResult } from "@/types/email";
import type {
  Contact,
  ContactActivityLog,
  ContactConsent,
  ContactProfile,
  ContactStatus,
  CreateContactPayload,
  IdentifierType,
  UpdateContactPayload,
} from "@/types/contacts";

export type { CreateContactPayload, UpdateContactPayload };

export interface GetContactsParams {
  page?: number;
  pageSize?: number;
  status?: ContactStatus;
  search?: string;
  sortBy?: "CreatedAt" | "UpdatedAt" | "LastActivityAt" | "FullName" | "Company";
  sortDirection?: "asc" | "desc";
}

class ContactService {
  async getContacts(accountId: string, params?: GetContactsParams): Promise<PagedResult<Contact>> {
    const q: Record<string, string | number | boolean> = {};
    if (params?.page !== undefined) q.page = params.page;
    if (params?.pageSize !== undefined) q.pageSize = params.pageSize;
    if (params?.status !== undefined) q.status = params.status;
    if (params?.search) q.search = params.search;
    if (params?.sortBy) q.sortBy = params.sortBy;
    if (params?.sortDirection) q.sortDirection = params.sortDirection;

    return apiClient.get(`/accounts/${accountId}/contacts`, {
      requiresAuth: true,
      params: Object.keys(q).length > 0 ? q : undefined,
    });
  }

  async getContact(accountId: string, contactId: string): Promise<ContactProfile> {
    return apiClient.get(`/accounts/${accountId}/contacts/${contactId}`, { requiresAuth: true });
  }

  async createContact(accountId: string, data: CreateContactPayload): Promise<Contact> {
    return apiClient.post(`/accounts/${accountId}/contacts`, data, { requiresAuth: true });
  }

  async updateContact(accountId: string, contactId: string, data: UpdateContactPayload): Promise<Contact> {
    return apiClient.patch(`/accounts/${accountId}/contacts/${contactId}`, data, { requiresAuth: true });
  }

  async deleteContact(accountId: string, contactId: string): Promise<void> {
    return apiClient.delete(`/accounts/${accountId}/contacts/${contactId}`, { requiresAuth: true });
  }

  async anonymizeContact(accountId: string, contactId: string): Promise<void> {
    return apiClient.post(`/accounts/${accountId}/contacts/${contactId}/anonymize`, undefined, { requiresAuth: true });
  }

  async addIdentifier(
    accountId: string,
    contactId: string,
    data: { identifierType: IdentifierType; value: string; isPrimary?: boolean; label?: string },
  ): Promise<Contact> {
    return apiClient.post(`/accounts/${accountId}/contacts/${contactId}/identifiers`, data, { requiresAuth: true });
  }

  async removeIdentifier(
    accountId: string,
    contactId: string,
    type: IdentifierType,
    value: string,
  ): Promise<void> {
    return apiClient.delete(
      `/accounts/${accountId}/contacts/${contactId}/identifiers/${type}/${encodeURIComponent(value)}`,
      { requiresAuth: true },
    );
  }

  async getAttributes(accountId: string, contactId: string): Promise<Record<string, string>> {
    return apiClient.get(`/accounts/${accountId}/contacts/${contactId}/attributes`, { requiresAuth: true });
  }

  async setAttributes(accountId: string, contactId: string, attributes: Record<string, string>): Promise<void> {
    return apiClient.put(`/accounts/${accountId}/contacts/${contactId}/attributes`, { attributes }, { requiresAuth: true });
  }

  async getConsents(accountId: string, contactId: string): Promise<ContactConsent[]> {
    return apiClient.get(`/accounts/${accountId}/contact-consent/${contactId}/consents`, { requiresAuth: true });
  }

  async getActivityLog(accountId: string, contactId: string): Promise<ContactActivityLog[]> {
    return apiClient.get(`/accounts/${accountId}/contacts/${contactId}/activity`, { requiresAuth: true });
  }
}

export const contactService = new ContactService();
